<script setup lang="ts">
import Loader from "~/components/Common/Loader.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { safePdfDocumentUrl, withCollapsedPdfNavigationPane } from "~/utils/helpers/pdf-viewer";
import type { IAttendanceDocument } from "~/utils/interfaces/attendance-review-interfaces";

const props = defineProps<{ document: IAttendanceDocument | null }>();
const emit = defineEmits<{ close: [] }>();

/*
 * Same rendering as the LMS document player and the program viewer: the browser's own PDF viewer
 * inside an iframe, which brings its own zoom and pager.
 * ponytail: the design draws custom `− 100 % +` / `Page 1 / 2` chrome and highlights the line each
 * row came from — neither is reachable from outside an iframe. That needs pdf.js with a text layer
 * plus per-line coordinates from the API; see the annotated-viewer phase.
 */
const isLoading = ref(true);
const viewerUrl = computed(() => safePdfDocumentUrl(props.document?.url));
const embeddedViewerUrl = computed(() => withCollapsedPdfNavigationPane(viewerUrl.value));
</script>

<template>
  <aside class="flex h-full w-[38%] min-w-[360px] shrink-0 flex-col border-r border-stroke-color bg-canvas-grey-color">
    <header class="flex h-10 shrink-0 items-center gap-2 border-b border-stroke-color bg-light-color px-4">
      <SvgIcon name="document" class="h-[16px] w-[16px] shrink-0 text-secondary-color" />
      <span class="body_regular truncate text-primary-color" :title="document?.fileName">
        {{ document?.fileName }}
      </span>
      <SvgIcon
        name="arrow_back"
        class="ml-auto h-[16px] w-[16px] shrink-0 cursor-pointer text-secondary-color"
        title="Masquer la feuille d'émargement"
        @click="emit('close')"
      />
    </header>

    <div class="relative flex-1">
      <div v-if="isLoading && viewerUrl" class="absolute inset-0 flex items-center justify-center">
        <Loader :width="32" :height="32" />
      </div>
      <iframe
        v-if="embeddedViewerUrl"
        :src="embeddedViewerUrl"
        title="Feuille d'émargement"
        width="100%"
        height="100%"
        frameborder="0"
        @load="isLoading = false"
      ></iframe>
      <div v-else class="flex h-full items-center justify-center px-6 text-center">
        <span class="body_regular text-secondary-color">Document indisponible</span>
      </div>
    </div>
  </aside>
</template>
